// hooks/useCamera.ts
import { useRef } from "react";
import logger from "utils/logger";

export function useCamera() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;

      // Bind stream to <video> and explicitly play it
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true; // required for autoplay on Chrome
        videoRef.current.playsInline = true; // iOS fix
        await videoRef.current.play().catch((err) => {
          logger.warn("Video play() failed:", err);
        });
      }
    } catch (err) {
      logger.error("Camera access denied or error:", err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = (callback: (blob: Blob, url: string) => void) => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(videoRef.current, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        callback(blob, url);
      }
    }, "image/png");
  };

  return { videoRef, startCamera, stopCamera, capturePhoto };
}
